import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { markNotificationRead, markAllNotificationsRead } from "../services/apiService";

const TYPE_ICONS = {
  new_feedback: "🐞",
  feedback_reply: "💬",
  feedback_status: "📋",
};

export default function NotificationBell({ notifications, setNotifications }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleClickNoti = async (noti) => {
    if (!noti.read) {
      await markNotificationRead(noti.id);
      setNotifications(prev => prev.map(n => n.id === noti.id ? { ...n, read: true } : n));
    }
    setOpen(false);
    // Notification links are stored relative to the daily app (e.g. "/admin"),
    // which now lives under /app.
    if (noti.link) navigate(noti.link.startsWith("/") ? `/app${noti.link}` : noti.link);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "vừa xong";
    if (mins < 60) return `${mins} phút`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} giờ`;
    const days = Math.floor(hours / 24);
    return `${days} ngày`;
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Bell button */}
      <button onClick={() => setOpen(!open)}
        style={{
          background: open ? "#3B82F612" : "transparent",
          border: "none",
          borderRadius: "10px", padding: "8px", cursor: "pointer",
          fontSize: "16px", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center",
          minWidth: "36px", height: "36px", position: "relative",
          color: open ? "#3B82F6" : "var(--text-muted)",
          transition: "all 0.2s",
        }}>
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: "absolute", top: "-4px", right: "-4px",
            background: "#EF4444", color: "#fff",
            fontSize: "9px", fontWeight: "800",
            minWidth: "16px", height: "16px",
            borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px",
          }}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "fixed", top: "60px", right: "8px", left: "8px",
          maxWidth: "360px", marginLeft: "auto",
          maxHeight: "70vh", overflowY: "auto",
          background: "var(--bg-card)", border: "1px solid var(--border-secondary)",
          borderRadius: "14px", boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
          zIndex: 1000,
        }}>
          {/* Header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 16px", borderBottom: "1px solid var(--border-secondary)",
          }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)" }}>
              Thông báo
            </span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "11px", color: "#3B82F6", fontWeight: "600" }}>
                Đánh dấu tất cả đã đọc
              </button>
            )}
          </div>

          {/* List */}
          {notifications.length === 0 ? (
            <div style={{ padding: "30px 16px", textAlign: "center", color: "var(--text-muted)", fontSize: "12px" }}>
              Chưa có thông báo nào
            </div>
          ) : (
            notifications.slice(0, 20).map(noti => (
              <div key={noti.id} onClick={() => handleClickNoti(noti)}
                style={{
                  display: "flex", gap: "10px", padding: "12px 16px",
                  cursor: "pointer", borderBottom: "1px solid var(--border-secondary)",
                  background: noti.read ? "transparent" : "var(--bg-card-alt)",
                  transition: "background 0.2s",
                }}>
                <div style={{ fontSize: "18px", flexShrink: 0, marginTop: "2px" }}>
                  {TYPE_ICONS[noti.type] || "🔔"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: "12px", fontWeight: noti.read ? "500" : "700",
                    color: noti.read ? "var(--text-secondary)" : "var(--text-primary)",
                    marginBottom: "2px",
                  }}>
                    {noti.title}
                  </div>
                  {noti.body && (
                    <div style={{
                      fontSize: "11px", color: "var(--text-muted)", lineHeight: 1.4,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {noti.body}
                    </div>
                  )}
                  <div style={{ fontSize: "10px", color: "var(--text-dim)", marginTop: "4px" }}>
                    {timeAgo(noti.created_at)}
                  </div>
                </div>
                {!noti.read && (
                  <div style={{
                    width: "8px", height: "8px", borderRadius: "50%",
                    background: "#3B82F6", flexShrink: 0, marginTop: "6px",
                  }} />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
